import React from 'react'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useOkta, OktaPasswordPolicy } from '@hooks/useOkta'
import { useSiteContext } from '@contexts/MkSiteContext'
import { SignInState, SignInFormData, SignInFormField } from '@account/types/signin'
import { SIGNIN_STEPS, getStepOrder } from '@account/components/SignIn/stepRegistry'
import { SIGNIN_STEP_KEYS } from '@constants'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import type { RegistrationContext } from '@account/hooks/useRegistration/_reducer'
import type { MkGenericValue } from '@typescript'
import type { SignInStepName } from '@account/components/SignIn/types'
import { getLockoutVariant, isUsernameLocked, isPasswordLockoutActive } from '@auth/utils/lockoutUtils'
import AuthenticationError from '@auth/errors/AuthenticationError'
import { useTrackEvent } from '@analytics/trackEvent'
import type { LoginAttemptEvent } from '@analytics/types'
import { AuthTokenProcessor } from '../../../../shared/services/AuthTokenProcessor'
import { AUTH_ERROR_TYPES } from '../../../../shared/constants/authentication/authErrorTypes'

// Helper function to create a SignIn form field
const createSignInField = (
  id: string,
  type: SignInFormField['type'],
  options: {
    label?: string
    value?: string | boolean
    required?: boolean
    placeholder?: string
    autoComplete?: string
    selectOptions?: Array<{ label: string; value: string }>
  } = {},
): SignInFormField => ({
  id,
  name: id,
  label: options.label || id,
  type,
  value: options.value ?? (type === 'checkbox' ? false : ''),
  isValid: true, // Start all fields as valid, validate on interaction (matching Register pattern)
  required: options.required ?? false,
  errorMessage: '',
  placeholder: options.placeholder,
  autoComplete: options.autoComplete,
  options: options.selectOptions,
})

// Initialize main SignIn form data (without localStorage values)
const createInitialSignInFormData = (): SignInFormData => ({
  username: createSignInField('username', 'text', {
    label: 'Username',
    required: true,
    placeholder: 'Enter your username',
    autoComplete: 'username',
    value: '',
  }),
  password: createSignInField('password', 'password', {
    label: 'Password',
    required: true,
    placeholder: 'Enter your password',
    autoComplete: 'current-password',
  }),
  newPassword: createSignInField('newPassword', 'password', {
    label: 'New Password',
    required: false,
    placeholder: 'Enter your new password',
    autoComplete: 'new-password',
  }),
  rememberMe: createSignInField('rememberMe', 'checkbox', {
    label: 'Remember Me',
    value: false,
  }),
  stateToken: createSignInField('stateToken', 'hidden', {
    label: 'State Token',
    required: false,
    value: '',
  }),
})

// Initialize recovery form data
const createInitialRecoveryFormData = (): SignInFormData => ({
  registerAsCompany: createSignInField('registerAsCompany', 'checkbox', {
    label: 'Register as Company',
    value: false,
  }),
  ownerNumber: createSignInField('ownerNumber', 'text', {
    label: 'Owner Number',
    required: true,
    placeholder: 'Enter your owner number',
  }),
  firstName: createSignInField('firstName', 'text', {
    label: 'First Name',
    required: true,
    placeholder: 'Enter your first name',
  }),
  lastName: createSignInField('lastName', 'text', {
    label: 'Last Name',
    required: true,
    placeholder: 'Enter your last name',
  }),
  companyName: createSignInField('companyName', 'text', {
    label: 'Company Name',
    placeholder: 'Enter your company name',
  }),
  verificationMethod: createSignInField('verificationMethod', 'radio', {
    label: 'Verification Method',
    required: true,
    selectOptions: [
      { label: 'Phone', value: 'phone' },
      { label: 'Contract', value: 'contract' },
    ],
  }),
  phone: createSignInField('phone', 'tel', {
    label: 'Phone Number',
    placeholder: 'Enter your phone number',
  }),
  phoneCountry: createSignInField('phoneCountry', 'text', {
    label: 'Phone Country',
    value: 'USA',
  }),
  contractNumber: createSignInField('contractNumber', 'text', {
    label: 'Contract Number',
    placeholder: 'Enter your contract number',
  }),
  // Fields for forgot password flow
  email: createSignInField('email', 'email', {
    label: 'Email Address',
    required: false,
    placeholder: 'Enter your email address',
    autoComplete: 'email',
  }),
  userName: createSignInField('userName', 'text', {
    label: 'Username',
    required: false,
    placeholder: 'Enter your username',
    autoComplete: 'username',
  }),
})

const initialState: SignInState = {
  currentStep: SIGNIN_STEP_KEYS.SIGNIN as SignInStepName,
  formData: createInitialSignInFormData(),
  loading: false,
  error: null,
  validationErrors: [],
  fieldErrors: {},
  // Add step-level state tracking like Register - using step names
  touchedByStep: {},
  stepValidationErrors: [],

  // Enhanced recovery state
  recoveredUsername: '',
  passwordResetEmail: '',
  newPassword: '',
  confirmPassword: '',
  accountLockoutTime: null,
  resendEmailCooldown: null,

  // Recovery form and loading states
  recoveryFormData: createInitialRecoveryFormData(),
  recoveryLoading: false,
  recoveryError: undefined,

  // Password policy and reset URL from Okta recovery token response
  passwordPolicy: null,
  resetPasswordUrl: null,
}

export const useSignIn = () => {
  const [state, setState] = React.useState<SignInState>(initialState)
  const authState = useAuthentication()
  const { login } = authState
  const { oktaAuth } = useOkta()
  const { authBrand } = useSiteContext()
  const trackEvent = useTrackEvent()

  // Load saved values from localStorage on mount
  React.useEffect(() => {
    const savedRememberMe = DataStore.getItem('rememberMe', { store: localStorage })
    const savedUsername = DataStore.getItem('savedUsername', { store: localStorage })

    // Load password policy and reset URL from DataStore (set by MkPageActivate)
    const passwordPolicy = DataStore.getItem('passwordPolicy')
    const resetPasswordUrl = DataStore.getItem('resetPasswordUrl')

    setState((prev) => {
      const updates: Partial<SignInState> = {}

      // Handle rememberMe and username
      if (savedRememberMe === 'true' && savedUsername) {
        updates.formData = {
          ...prev.formData,
          username: {
            ...prev.formData.username,
            value: savedUsername,
            isValid: Boolean(savedUsername),
            errorMessage: '',
          },
          rememberMe: {
            ...prev.formData.rememberMe,
            value: true,
          },
        }
      }

      // Handle password policy and reset URL
      if (passwordPolicy) {
        updates.passwordPolicy = passwordPolicy
      }
      if (resetPasswordUrl) {
        updates.resetPasswordUrl = resetPasswordUrl
      }

      return { ...prev, ...updates }
    })
  }, [])

  // Utility functions for common patterns
  const setRecoveryLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, recoveryLoading: loading }))
  }, [])

  const setRecoveryError = React.useCallback((error: { message: string; code?: string } | undefined) => {
    setState((prev) => ({ ...prev, recoveryError: error }))
  }, [])

  const updateRecoveryState = React.useCallback((updates: Partial<SignInState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  // Abstracted async operation with loading and error handling
  const executeRecoveryOperation = React.useCallback(
    async <T>(operation: () => Promise<T>, errorCode: string, successCallback?: (result: T) => void): Promise<T | undefined> => {
      setRecoveryLoading(true)
      setRecoveryError(undefined)

      try {
        const result = await operation()
        if (successCallback) {
          successCallback(result)
        }
        return result
      } catch (err: unknown) {
        let errorMessage = 'An error occurred. Please try again.'

        // Type guard to check if error has API response structure
        const isApiError = (error: unknown): error is { response?: { data?: { errors?: Array<{ message?: string }> } }; message?: string } =>
          error !== null && typeof error === 'object' && ('response' in error || 'message' in error)

        if (isApiError(err)) {
          if (err?.response?.data?.errors?.[0]) {
            const apiErrorData = err.response.data.errors[0]
            errorMessage = apiErrorData.message || errorMessage
          } else if (err?.message) {
            errorMessage = err.message
          }
        } else if (err instanceof Error) {
          errorMessage = err.message
        }

        setRecoveryError({ message: errorMessage, code: errorCode })
        return undefined
      } finally {
        setRecoveryLoading(false)
      }
    },
    [setRecoveryLoading, setRecoveryError],
  )

  const updateField = React.useCallback((name: string, value: any) => {
    setState((prev) => {
      const existingField = prev.formData[name]
      if (!existingField) return prev

      // Process value based on field type
      let processedValue: string | boolean = ''
      if (existingField.type === 'checkbox') {
        processedValue = Boolean(value)
      } else {
        processedValue = String(value || '')
      }

      // Basic validation
      const isEmpty = !processedValue || (typeof processedValue === 'string' && processedValue.trim().length === 0)
      const isValid = !existingField.required || !isEmpty
      const errorMessage = !isValid ? `${existingField.label} is required` : ''

      const updatedField: SignInFormField = {
        ...existingField,
        value: processedValue,
        isValid,
        errorMessage,
      }

      const newFormData = { ...prev.formData, [name]: updatedField }

      // Update field errors
      const newFieldErrors = { ...prev.fieldErrors }
      if (!isValid) {
        newFieldErrors[name] = errorMessage
      } else {
        delete newFieldErrors[name]
      }

      // Mark current step as touched
      const newTouchedByStep = { ...prev.touchedByStep, [prev.currentStep]: true }

      // Clear step validation errors on any user edit
      const newStepValidationErrors: string[] = []

      // Handle rememberMe checkbox localStorage persistence right before dispatch
      if (name === 'rememberMe') {
        if (processedValue) {
          DataStore.setItem('rememberMe', 'true', { store: localStorage })
        } else {
          DataStore.removeItem('rememberMe', { store: localStorage })
        }
      }

      // Handle username field localStorage persistence right before dispatch
      if (name === 'username') {
        const rememberMeValue = prev.formData.rememberMe?.value
        if (rememberMeValue && processedValue) {
          DataStore.setItem('savedUsername', String(processedValue), { store: localStorage })
        } else if (!rememberMeValue) {
          // Only remove if rememberMe is explicitly false
          DataStore.removeItem('savedUsername', { store: localStorage })
        }
      }

      return {
        ...prev,
        formData: newFormData,
        fieldErrors: newFieldErrors,
        touchedByStep: newTouchedByStep,
        stepValidationErrors: newStepValidationErrors,
      }
    })
  }, [])

  // Update a recovery field (compatible with Registration formData shape)
  const updateRecoveryField = React.useCallback((fieldName: string, value: MkGenericValue) => {
    setState((prev) => {
      if (!prev.recoveryFormData) return prev

      const existing = prev.recoveryFormData[fieldName] || {
        id: fieldName,
        name: fieldName,
        label: fieldName,
        type: 'text' as const,
        value: '',
        isValid: false,
        required: false,
        errorMessage: '',
        options: [],
      }

      // Basic validity: non-empty for strings, true for booleans, number not NaN
      let isValid = true
      let processedValue: string | boolean = ''

      if (typeof value === 'string') {
        processedValue = value
        isValid = value.trim().length > 0
      } else if (typeof value === 'boolean') {
        processedValue = value
        isValid = true
      } else if (typeof value === 'number') {
        processedValue = String(value)
        isValid = Number.isFinite(value)
      } else {
        processedValue = ''
        isValid = false
      }

      return {
        ...prev,
        recoveryFormData: {
          ...prev.recoveryFormData,
          [fieldName]: {
            ...existing,
            value: processedValue,
            isValid,
            required: Boolean(existing.required),
          },
        },
      } as SignInState
    })
  }, [])

  // Helper to adapt various input event/value shapes (similar to register flow)
  const handleRecoveryInputValueChange = React.useCallback(
    (fieldName: string) => (input: any) => {
      let newVal: any = input
      if (input && typeof input === 'object') {
        if ('target' in input && (input as any).target?.value !== undefined) {
          newVal = (input as any).target.value
        } else if ('value' in input) {
          newVal = (input as any).value
        }
      }
      updateRecoveryField(fieldName, newVal)
    },
    [updateRecoveryField],
  )

  const setCurrentStep = React.useCallback((stepName: SignInStepName) => {
    setState((prev) => ({ ...prev, currentStep: stepName }))
  }, [])

  const nextStep = React.useCallback(() => {
    setState((prev) => {
      const stepOrder = getStepOrder()
      const currentIndex = stepOrder.indexOf(prev.currentStep)
      const nextStepName = stepOrder[Math.min(currentIndex + 1, stepOrder.length - 1)]
      return { ...prev, currentStep: nextStepName }
    })
  }, [])

  const previousStep = React.useCallback(() => {
    setState((prev) => {
      const stepOrder = getStepOrder()
      const currentIndex = stepOrder.indexOf(prev.currentStep)
      const prevStepName = stepOrder[Math.max(currentIndex - 1, 0)]
      return { ...prev, currentStep: prevStepName }
    })
  }, [])

  const setLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loading }))
  }, [])

  const setError = React.useCallback((message: string, raw?: Error) => {
    setState((prev) => ({ ...prev, error: { message, raw } }))
  }, [])

  const clearError = React.useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  const setValidationErrors = React.useCallback((errors: string[]) => {
    setState((prev) => ({ ...prev, validationErrors: errors }))
  }, [])

  const setFieldError = React.useCallback((field: string, message: string) => {
    setState((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, [field]: message },
    }))
  }, [])

  const clearFieldErrors = React.useCallback(() => {
    setState((prev) => ({ ...prev, fieldErrors: {} }))
  }, [])

  // Reset function to restore initial state
  const resetContext = React.useCallback(() => {
    setState(initialState)
  }, [])

  // Enhanced validation logic following Register pattern
  const isStepTouched = React.useCallback(() => Boolean(state.touchedByStep?.[state.currentStep]), [state.touchedByStep, state.currentStep])

  const canSubmit = React.useCallback(() => {
    const { username, password, newPassword } = state.formData
    const hasBasicData = String(username?.value || '').trim().length > 0 && String(password?.value || '').trim().length > 0
    const hasNewPasswordData = String(newPassword?.value || '').trim().length > 0

    // Handle reset password step differently
    if (state.currentStep === (SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM as SignInStepName)) {
      // For reset password, only need newPassword
      if (!isStepTouched()) return hasNewPasswordData
      return hasNewPasswordData && newPassword?.isValid
    }

    // Only validate if step has been touched (following Register pattern)
    if (!isStepTouched()) return hasBasicData

    // Check field validation
    return hasBasicData && username?.isValid && password?.isValid
  }, [state.formData, isStepTouched])

  const canGoNext = React.useCallback(() => {
    // Only validate if step has been touched (following Register pattern)
    if (!isStepTouched()) return false
    return canSubmit()
  }, [isStepTouched, canSubmit])

  const canGoPrevious = React.useCallback(() => {
    const stepOrder = getStepOrder()
    return stepOrder.indexOf(state.currentStep) > 0
  }, [state.currentStep])

  // Determine if the recovery form has enough information to proceed
  const recoveryCanSubmit = React.useCallback(() => {
    const form = state.recoveryFormData
    if (!form) return false

    const registerAsCompany = Boolean(form.registerAsCompany?.value)
    const ownerOk = String(form.ownerNumber?.value || '').trim().length > 0
    const nameOk = registerAsCompany
      ? String(form.companyName?.value || '').trim().length > 0
      : String(form.firstName?.value || '').trim().length > 0 && String(form.lastName?.value || '').trim().length > 0
    const method = (form.verificationMethod?.value as 'phone' | 'contract' | null) || null
    const phoneOk = String(form.phone?.value || '').trim().length > 0
    const contractOk = String(form.contractNumber?.value || '').trim().length > 0

    // Fix nested ternary
    let methodOk = false
    if (method === 'phone') {
      methodOk = phoneOk
    } else if (method === 'contract') {
      methodOk = contractOk
    }

    return ownerOk && nameOk && methodOk
  }, [state.recoveryFormData])

  // Password reset validation
  const canResetPassword = React.useCallback(() => {
    const { newPassword = '', confirmPassword = '' } = state
    return newPassword.trim().length > 0 && confirmPassword.trim().length > 0 && newPassword === confirmPassword && newPassword.length >= 8
  }, [state.newPassword, state.confirmPassword])

  // Check if account is locked
  const isAccountLocked = React.useCallback(() => {
    if (!state.accountLockoutTime) return false
    const now = new Date()
    const lockoutEnd = new Date(state.accountLockoutTime.getTime() + 15 * 60 * 1000) // 15 minutes
    return now < lockoutEnd
  }, [state.accountLockoutTime])

  // Check if resend email is available
  const canResendEmail = React.useCallback(() => {
    if (!state.resendEmailCooldown) return true
    const now = new Date()
    const cooldownEnd = new Date(state.resendEmailCooldown.getTime() + 3 * 60 * 1000) // 3 minutes
    return now >= cooldownEnd
  }, [state.resendEmailCooldown])

  // Get remaining lockout time in minutes
  const getRemainingLockoutTime = React.useCallback(() => {
    if (!state.accountLockoutTime) return 0
    const now = new Date()
    const lockoutEnd = new Date(state.accountLockoutTime.getTime() + 15 * 60 * 1000)
    const remaining = Math.max(0, Math.ceil((lockoutEnd.getTime() - now.getTime()) / (1000 * 60)))
    return remaining
  }, [state.accountLockoutTime])

  // Get remaining resend cooldown time in minutes
  const getRemainingResendTime = React.useCallback(() => {
    if (!state.resendEmailCooldown) return 0
    const now = new Date()
    const cooldownEnd = new Date(state.resendEmailCooldown.getTime() + 3 * 60 * 1000)
    const remaining = Math.max(0, Math.ceil((cooldownEnd.getTime() - now.getTime()) / (1000 * 60)))
    return remaining
  }, [state.resendEmailCooldown])

  // Abstracted main login operation
  const executeLoginOperation = React.useCallback(
    async (credentials: { username: string; password: string; rememberMe: boolean }) => {
      setLoading(true)
      clearError()

      // Fire LOGIN_ATTEMPT before any guards (lockout, etc.) so analytics captures every attempt.
      trackEvent<LoginAttemptEvent>({ type: 'LOGIN_ATTEMPT', payload: {} })

      // Clear any existing registration context to prevent username confusion from previous login attempts
      ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, { store: localStorage })

      try {
        const { username, password, rememberMe } = credentials

        // Check if this username is already locked before making API calls
        const lockoutVariant = getLockoutVariant(authState)
        if (lockoutVariant !== 'NONE' && isUsernameLocked(authState, username)) {
          // For MFA lockouts, always block (no time expiration)
          if (lockoutVariant === 'MFA_CRITICAL') {
            setCurrentStep(SIGNIN_STEP_KEYS.ACCOUNT_LOCKED as SignInStepName)
            throw new AuthenticationError(AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED)
          }

          // For password lockouts, only block if still within lockout time
          if (lockoutVariant === 'PASSWORD_TEMPORARY' && isPasswordLockoutActive(authState)) {
            setCurrentStep(SIGNIN_STEP_KEYS.ACCOUNT_LOCKED as SignInStepName)
            throw new AuthenticationError(AUTH_ERROR_TYPES.ACCOUNT_TEMPORARILY_LOCKED)
          }

          // If lockout has expired, continue with normal login flow
        }

        // Handle remember me functionality
        if (rememberMe) {
          DataStore.setItem('rememberMe', 'true', { store: localStorage })
          DataStore.setItem('savedUsername', username, { store: localStorage })
        } else {
          DataStore.removeItem('rememberMe', { store: localStorage })
        }

        await login({ username, password })

        // After successful login, if remember me is unchecked, clear the saved username
        if (!rememberMe) {
          DataStore.removeItem('savedUsername', { store: localStorage })
        }

        // Clear any validation errors on success
        setValidationErrors([])

        return true
      } catch (err) {
        const error = err as Error
        setError(error.message, error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [login, setLoading, clearError, setValidationErrors, setError, authState, setCurrentStep, trackEvent],
  )

  const submit = React.useCallback(async () => {
    if (!canSubmit()) return

    const currentStep = SIGNIN_STEPS.get(state.currentStep)

    // Handle forgot-password-reset-form step with Okta API
    if (state.currentStep === (SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM as SignInStepName) && currentStep?.props?.apiConfig) {
      const { apiConfig } = currentStep.props
      const { endpoint, method, headers = {}, body } = apiConfig as any

      try {
        const requestBody = body?.(state.formData)

        // Resolve endpoint - support both string and function
        const resolvedEndpoint = typeof endpoint === 'function' ? endpoint() : endpoint

        const response = await fetch(resolvedEndpoint, {
          method,
          headers: {
            ...headers,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))

          // Use centralized error parsing but set local error for immediate display
          const { parseOktaError } = await import('@utils/oktaErrorHandler')
          const enhancedError = parseOktaError(errorData, response.status, response.statusText)

          // Set the enhanced error in local state for immediate display
          setError(enhancedError.message, enhancedError)
          return
        }

        const data = await response.json()

        // Handle success - continue with OAuth flow
        if (data?.sessionToken && oktaAuth) {
          // Complete the OAuth flow with the session token (same as Register)
          try {
            const { codeVerifier } = await oktaAuth.token.prepareTokenParams({ sessionToken: String(data.sessionToken) })

            // Get user info for the session
            const user = AuthTokenProcessor.extractUserFromResponse(data)
            const username = String(user?.login || state.formData.username?.value || 'unknown')

            // Store authentication session (following Register pattern - lines 246-257)
            const authenticatedSession = {
              sessionType: 'authenticated',
              authBrand,
              authenticatedUser: user || {},
              codeVerifier: String(codeVerifier),
              sessionToken: String(data.sessionToken),
              username,
              recoveryToken: '',
            }

            ProtectedDataStore.setItem(EMkStorageKeys.MAKO_SESSION, { ...authenticatedSession })

            // Save rememberMe and username for persistence after OAuth callback
            DataStore.setItem('rememberMe', 'true', { store: localStorage })
            DataStore.setItem('savedUsername', username, { store: localStorage })

            // Clear any cached Okta tokens/sessions before OAuth redirect
            // This prevents stale sessions from interfering with password reset flow
            await oktaAuth.tokenManager.clear()
            DataStore.bulkRemoveItems(['okta-cache-storage', 'okta-shared-transaction-storage', 'okta-original-uri-storage', 'okta-token-storage', 'okta-token-storage-keys'], {
              store: localStorage,
            })

            // Complete the authentication with redirect to home
            await oktaAuth.token.getWithRedirect({
              sessionToken: String(data.sessionToken),
              codeVerifier: String(codeVerifier),
            })

            return
          } catch (authError) {
            // Fall through to next step
          }
        }

        // Handle MFA_REQUIRED status - user has existing MFA enrollment and needs to verify
        if (data?.status === 'MFA_REQUIRED' && data?.stateToken) {
          // Clear any existing registration context to prevent username confusion from previous login attempts
          ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, { store: localStorage })

          // eslint-disable-next-line no-underscore-dangle
          const embedded = data?._embedded || {}
          const enrolledFactors = embedded.factors || []
          const username = embedded.user?.profile?.login || ''
          const email = embedded.user?.profile?.email || username

          // Use the first enrolled factor (Okta typically returns the primary factor)
          const primaryFactor = enrolledFactors[0]
          if (!primaryFactor) {
            setError('MFA verification failed: No enrolled factors found')
            return
          }

          // eslint-disable-next-line no-underscore-dangle
          const verifyLink = primaryFactor._links?.verify?.href
          const phoneNumber = primaryFactor.profile?.phoneNumber || ''
          const mfaEmail = primaryFactor.factorType === 'email' ? primaryFactor.profile?.email : undefined

          // Save to registration context with all necessary MFA data
          // Note: Email is NOT saved to localStorage for security reasons
          // Email should be kept in React state (formData) only
          const now = Date.now()
          const context: RegistrationContext = {
            stateToken: data.stateToken,
            activationToken: '',
            registrationToken: '',
            // Email is intentionally NOT saved to localStorage for security
            userName: username,
            createdAt: now,
            expiresAt: now + 24 * 60 * 60 * 1000, // 24 hours
            mfaFactors: enrolledFactors,
            selectedFactor: primaryFactor,
            mfaFactorId: primaryFactor.id,
            factorProfile: primaryFactor.profile,
            phone: phoneNumber,
            mfaEmail, // mfaEmail is acceptable as it's MFA-specific, not the primary email
            mfaRequired: true,
          }

          ProtectedDataStore.setItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, context, {
            store: localStorage,
            expirationTime: context.expiresAt,
          })

          // Store the factor ID and verify link for verification step
          DataStore.setItem('mfaFactorId', primaryFactor.id)
          if (verifyLink) {
            DataStore.setItem('mfaNextLink', verifyLink)
          }

          // Trigger verification code send by calling the verify endpoint
          if (verifyLink) {
            try {
              const verifyResponse = await fetch(`${window.location.origin}/mkapi/v1/okta/authn/factors/${primaryFactor.id}/verify`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  stateToken: data.stateToken,
                }),
              })

              if (verifyResponse.ok) {
                const responseData = await verifyResponse.json()

                // Extract verify link from response
                // eslint-disable-next-line no-underscore-dangle
                const updatedVerifyLink = Array.isArray(responseData._links?.verify) ? responseData._links.verify[0]?.href : responseData._links?.verify?.href

                // Extract resend link from response for later use
                // eslint-disable-next-line no-underscore-dangle
                const resendLink = Array.isArray(responseData._links?.resend) ? responseData._links.resend[0]?.href : responseData._links?.resend?.href

                if (updatedVerifyLink || resendLink) {
                  // Update registration context with both verify and resend links
                  const updatedContext = {
                    ...context,
                    ...(updatedVerifyLink && { verifyLink: updatedVerifyLink }),
                    ...(resendLink && { resendLink }),
                  }

                  ProtectedDataStore.setItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, updatedContext, {
                    store: localStorage,
                    expirationTime: context.expiresAt,
                  })
                }
              }
            } catch (triggerError) {
              // Log but don't block - user can still proceed to verification step
              console.warn('Failed to trigger verification code send:', triggerError)
            }
          }

          // Check rememberMe and save username to localStorage
          const savedRememberMe = DataStore.getItem('rememberMe', { store: localStorage })
          if (savedRememberMe === 'true' && username) {
            DataStore.setItem('savedUsername', username, { store: localStorage })
          }

          // Dispatch event to trigger modal transition to VERIFY CODE step (not selector)
          window.dispatchEvent(
            new CustomEvent('passwordResetMfaVerificationRequired', {
              detail: { stateToken: data.stateToken, mfaFactors: enrolledFactors },
            }),
          )

          return
        }

        if (data?.status === 'SUCCESS') {
          // Extract user using existing utility (DRY principle)
          const user = AuthTokenProcessor.extractUserFromResponse(data)
          const username = user?.login || String(state.formData.username?.value || '')

          // Save username to localStorage using existing pattern (lines 312, 559, 753)
          if (username) {
            DataStore.setItem('savedUsername', username, { store: localStorage })
            DataStore.setItem('rememberMe', 'true', { store: localStorage })
          }

          // Dispatch event following existing MFA flow pattern (lines 757-761)
          window.dispatchEvent(
            new CustomEvent('passwordResetSuccess', {
              detail: { username },
            }),
          )

          return
        }
      } catch (error: unknown) {
        // Use centralized error parsing for catch block errors too
        const { parseOktaError } = await import('@utils/oktaErrorHandler')

        // Type guard to check if error has Okta API error properties
        const isOktaApiError = (
          err: unknown,
        ): err is {
          errorCode?: string
          errorSummary?: string
          errorCauses?: Array<{ errorSummary: string }>
          errorLink?: string
          errorId?: string
          status?: number
          statusText?: string
        } => err !== null && typeof err === 'object' && ('errorCode' in err || 'errorSummary' in err || 'errorCauses' in err)

        const errorData = isOktaApiError(error)
          ? {
              errorCode: error.errorCode,
              errorSummary: error.errorSummary,
              errorCauses: error.errorCauses,
              errorLink: error.errorLink,
              errorId: error.errorId,
            }
          : {
              errorSummary: error instanceof Error ? error.message : 'An unexpected error occurred',
            }

        const enhancedError = parseOktaError(errorData, isOktaApiError(error) ? error.status : undefined, isOktaApiError(error) ? error.statusText : undefined)

        setError(enhancedError.message, enhancedError)
      }
      return
    }

    const { username, password, rememberMe } = state.formData
    try {
      await executeLoginOperation({
        username: String(username?.value || ''),
        password: String(password?.value || ''),
        rememberMe: Boolean(rememberMe?.value || false),
      })
    } catch (error) {
      // Error is already handled by executeLoginOperation's setError
      // MFA errors should be caught here to prevent infinite loop
      if (error instanceof Error && (error.message === 'MFA_VERIFICATION_REQUIRED' || error.message === 'MFA_ENROLL_REQUIRED')) {
        // Don't re-throw MFA errors - they're expected flow control
      }
      // For other errors, they're already displayed via setError in executeLoginOperation
    }
  }, [state.formData, state.currentStep, canSubmit, executeLoginOperation, oktaAuth, nextStep, setError])

  // Enhanced recovery submission with step-specific logic
  const submitRecovery = React.useCallback(async () => {
    if (!recoveryCanSubmit()) return

    await executeRecoveryOperation(
      async () => {
        // TODO: integrate with actual recovery API endpoint
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 1000)
        })
        return 'recovered_username_123'
      },
      'FALLBACK_ERROR',
      (username) => {
        updateRecoveryState({ recoveredUsername: username })
        nextStep()
      },
    )
  }, [recoveryCanSubmit, nextStep, executeRecoveryOperation, updateRecoveryState])

  // Password reset email submission
  const submitPasswordResetEmail = React.useCallback(
    async (email: string) => {
      await executeRecoveryOperation(
        async () => {
          // TODO: integrate with actual password reset API endpoint
          await new Promise<void>((resolve) => {
            setTimeout(() => resolve(), 1000)
          })
          return { email, timestamp: new Date() }
        },
        'PASSWORD_RESET_ERROR',
        (result) => {
          updateRecoveryState({
            passwordResetEmail: result.email,
            resendEmailCooldown: result.timestamp,
          })
          nextStep()
        },
      )
    },
    [nextStep, executeRecoveryOperation, updateRecoveryState],
  )

  // Resend password reset email
  const resendPasswordResetEmail = React.useCallback(async () => {
    if (!canResendEmail()) return

    await executeRecoveryOperation(
      async () => {
        // TODO: integrate with actual resend API endpoint
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 1000)
        })
        return new Date()
      },
      'RESEND_ERROR',
      (timestamp) => {
        updateRecoveryState({ resendEmailCooldown: timestamp })
      },
    )
  }, [canResendEmail, executeRecoveryOperation, updateRecoveryState])

  // Submit new password
  const submitNewPassword = React.useCallback(async () => {
    if (!canResetPassword()) return

    await executeRecoveryOperation(
      async () => {
        // TODO: integrate with actual password reset API endpoint
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 1000)
        })
        return true
      },
      'PASSWORD_UPDATE_ERROR',
      () => {
        nextStep()
      },
    )
  }, [canResetPassword, nextStep, executeRecoveryOperation])

  // Handle account lockout
  const handleAccountLockout = React.useCallback(() => {
    setState((prev) => ({ ...prev, accountLockoutTime: new Date() }))
  }, [])

  // Copy username to clipboard
  const copyUsernameToClipboard = React.useCallback(async () => {
    const username = state.recoveredUsername
    if (username) {
      try {
        await navigator.clipboard.writeText(username)
        return true
      } catch (err) {
        return false
      }
    }
    return false
  }, [state.recoveredUsername])

  // Go back to sign in with recovered username
  const goBackToSignInWithUsername = React.useCallback(() => {
    const username = state.recoveredUsername
    if (username) {
      updateField('username', username)
      setCurrentStep(SIGNIN_STEP_KEYS.SIGNIN as SignInStepName) // Go back to signin step
    }
  }, [state.recoveredUsername, updateField, setCurrentStep])

  // Update new password field
  const setNewPassword = React.useCallback((password: string) => {
    setState((prev) => ({ ...prev, newPassword: password }))
  }, [])

  // Update confirm password field
  const setConfirmPassword = React.useCallback((password: string) => {
    setState((prev) => ({ ...prev, confirmPassword: password }))
  }, [])

  // Update password policy field
  const setPasswordPolicy = React.useCallback((policy: OktaPasswordPolicy | null) => {
    setState((prev) => ({ ...prev, passwordPolicy: policy }))
  }, [])

  // Update reset password URL field
  const setResetPasswordUrl = React.useCallback((url: string | null) => {
    setState((prev) => ({ ...prev, resetPasswordUrl: url }))
  }, [])

  // API execution logic for recovery operations
  const executeRecoveryApi = React.useCallback(
    async (
      operation: 'forgotUsername' | 'forgotPassword' | 'emailVerification' | 'tempCodeVerification' | 'securityQuestions' | 'accountSearch' | 'passwordReset',
      formData: SignInFormData,
      brand?: string,
    ) => {
      try {
        setLoading(true)
        clearError()

        // Get the current step to access API configuration
        const currentStep = SIGNIN_STEPS.get(state.currentStep)
        const apiConfig = (currentStep?.props?.apiConfig as any)?.[operation]

        if (!apiConfig) {
          throw new Error(`API configuration not found for operation: ${operation}`)
        }

        // Prepare request parameters
        // Resolve endpoint - support both string and function
        let url = typeof apiConfig.endpoint === 'function' ? apiConfig.endpoint() : apiConfig.endpoint
        const requestOptions: RequestInit = {
          method: apiConfig.method,
          headers: {
            'Content-Type': 'application/json',
            ...(apiConfig.headers || {}),
            ...(brand && { 'X-BRANDID': brand }),
          },
        }

        // Handle GET vs POST requests
        if (apiConfig.method === 'GET' && apiConfig.params) {
          const params = new URLSearchParams(apiConfig.params(formData))
          url += `?${params.toString()}`
        } else if (apiConfig.body) {
          requestOptions.body = JSON.stringify(apiConfig.body(formData))
        }

        // Execute the API call
        // BUG: MAK-2836
        const response = await fetch(url, requestOptions)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `API Error: ${response.status}`)
        }

        const data = await response.json()

        // Handle success based on operation
        switch (operation) {
          case 'forgotUsername':
            // Check if the response indicates an error state
            if (data.status === 'error' || (data.error && data.error !== 'null')) {
              // Handle structured error response
              const message = data.message && data.message !== 'null' ? data.message : ''
              const error = data.error && data.error !== 'null' ? data.error : ''
              const errorMessage = message || error || 'Username recovery failed'
              const { businessSystemCode } = data

              // Create enhanced error message for specific business codes
              let enhancedErrorMessage = errorMessage
              if (businessSystemCode === 'GPMBV003') {
                enhancedErrorMessage = 'Your account is currently inactive. Please contact customer support for assistance.'
              } else if (businessSystemCode === 'GPMBV002') {
                enhancedErrorMessage = 'No account found with the provided member information. Please verify your details and try again.'
              } else if (businessSystemCode) {
                enhancedErrorMessage = `${errorMessage} (Error Code: ${businessSystemCode})`
              }

              throw new Error(enhancedErrorMessage)
            }

            // Success case - username was recovered
            if (data.userName) {
              setState((prev) => ({ ...prev, recoveredUsername: data.userName }))
              // Save the recovered username to localStorage
              DataStore.setItem('savedUsername', data.userName, { store: localStorage })
              // Navigate to username confirmation step
              setCurrentStep(SIGNIN_STEP_KEYS.FORGOT_USERNAME_CONFIRMATION as SignInStepName)
            } else {
              throw new Error('Username not found. Please verify your information and try again.')
            }
            break

          case 'forgotPassword': {
            // Store username for potential resend operations
            const username = String((formData.userName as SignInFormField)?.value || (formData.email as SignInFormField)?.value || '')
            setState((prev) => ({ ...prev, recoveryUsername: username }))
            // Navigate to password reset link step
            setCurrentStep(SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_LINK as SignInStepName)
            break
          }

          case 'emailVerification':
            // Store verification code for next step
            setState((prev) => ({ ...prev, tempVerificationCode: data.verificationCode }))
            // Navigate to password reset form
            setCurrentStep(SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM as SignInStepName)
            break

          case 'tempCodeVerification':
            if (data.verified) {
              // Navigate to password reset form
              setCurrentStep(SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM as SignInStepName)
            } else {
              setError(data.message || 'Code verification failed')
            }
            break

          case 'securityQuestions':
            if (data.validated) {
              // Navigate to password reset form
              setCurrentStep(SIGNIN_STEP_KEYS.FORGOT_PASSWORD_RESET_FORM as SignInStepName)
            } else {
              setError(data.message || 'Security questions validation failed')
            }
            break

          case 'accountSearch':
            // Handle account search results - navigate to help panel
            if (data.results && data.results.length > 0) {
              setState((prev) => ({ ...prev, accountSearchResults: data.results }))
              // Navigate to help panel to show results
              setCurrentStep(SIGNIN_STEP_KEYS.HELP_PANEL as SignInStepName)
            } else {
              setError('No accounts found matching the criteria')
            }
            break

          default:
            // Handle any unexpected operations
            break
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
        setError(errorMessage)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [setLoading, setError, setState, setCurrentStep, state.currentStep],
  )

  return {
    state,
    updateField,
    setCurrentStep,
    nextStep,
    previousStep,
    setLoading,
    setError,
    clearError,
    setValidationErrors,
    setFieldError,
    clearFieldErrors,
    resetContext,
    canSubmit,
    canGoNext,
    canGoPrevious,
    isStepTouched,
    submit,
    recoveryFormData: state.recoveryFormData,
    updateRecoveryField,
    handleRecoveryInputValueChange,
    recoveryCanSubmit,
    submitRecovery,
    recoveryLoading: state.recoveryLoading,
    recoveryError: state.recoveryError,
    // Enhanced recovery state
    recoveredUsername: state.recoveredUsername,
    passwordResetEmail: state.passwordResetEmail,
    newPassword: state.newPassword,
    setNewPassword,
    confirmPassword: state.confirmPassword,
    setConfirmPassword,
    accountLockoutTime: state.accountLockoutTime,
    resendEmailCooldown: state.resendEmailCooldown,
    canResetPassword,
    isAccountLocked,
    canResendEmail,
    getRemainingLockoutTime,
    getRemainingResendTime,
    submitPasswordResetEmail,
    resendPasswordResetEmail,
    submitNewPassword,
    handleAccountLockout,
    copyUsernameToClipboard,
    goBackToSignInWithUsername,

    // Password policy and reset URL management
    passwordPolicy: state.passwordPolicy,
    resetPasswordUrl: state.resetPasswordUrl,
    setPasswordPolicy,
    setResetPasswordUrl,

    // API execution for recovery operations
    executeRecoveryApi,
  }
}

export type UseSignInReturn = ReturnType<typeof useSignIn>
